import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { logEvent } from "@/hooks/use-event-log";

export type SystemConfigKey =
  | "hourly_rate"
  | "default_overhead_pct"
  | "overhead_pct"
  | "provision_rate"
  | "reporting_reconciliation_day"
  /** Bedriftsdetaljer (VIB-710) — vises i Organisasjon-innstillinger og brukes evt. på faktura/PPTX-eksport. */
  | "crm_style_guide"
  | "company_name"
  | "company_org_nr"
  | "company_address"
  | "company_postal_code"
  | "company_city"
  | "company_phone"
  | "company_email"
  | "company_website"
  /** CRM-3.0: månedlig kostnadstak per signalagent når agenten ikke har eget tak. */
  | "crm_agent_cost_cap_default_nok";

interface SystemConfigRow {
  key: string;
  value: unknown;
  updated_by: string | null;
  updated_at: string;
}

/**
 * Henter ALLE rader i system_config (VIB-1396: brukes av Driftskonfig-
 * seksjonen i Funksjonsbrytere-panelet til å vise øvrige system_config-
 * nøkler read-only). Egen queryKey (ikke ["system-config", key]) siden dette
 * er en full-tabell-lesing, ikke per-nøkkel.
 */
export function useAllSystemConfig() {
  return useQuery({
    queryKey: ["system-config-all"],
    queryFn: async (): Promise<SystemConfigRow[]> => {
      const { data, error } = await supabase
        .from("system_config")
        .select("key, value, updated_by, updated_at")
        .order("key");
      if (error) throw error;
      return (data ?? []) as SystemConfigRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Reads a numeric value from the system_config key/value store.
 * Returns null while loading or if the key does not exist.
 */
export function useSystemConfigNumber(key: SystemConfigKey) {
  return useQuery({
    queryKey: ["system-config", key],
    queryFn: async (): Promise<number | null> => {
      const { data, error } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const n = Number(data.value);
      return Number.isFinite(n) ? n : null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Upserts a numeric value into system_config.
 * Stores as jsonb so values round-trip cleanly via the Supabase client.
 */
export function useUpdateSystemConfigNumber() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();

  return useMutation({
    mutationFn: async (params: { key: SystemConfigKey; value: number }) => {
      const row: Partial<SystemConfigRow> = {
        key: params.key,
        value: params.value,
        updated_by: activeUser?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from("system_config")
        .upsert(row, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(queryClient, "system_config", { key: variables.key });
      logEvent({
        event_type: "system_config.updated",
        entity_type: "system_config",
        entity_id: variables.key,
        user_id: activeUser?.id,
        payload: { key: variables.key, value: variables.value },
      });
    },
  });
}

/**
 * Reads a string value from the system_config key/value store.
 * Returns empty string while loading or if the key does not exist —
 * gjør UI-binding triviell (controlled inputs takler ikke null).
 */
export function useSystemConfigString(key: SystemConfigKey) {
  return useQuery({
    queryKey: ["system-config", key],
    queryFn: async (): Promise<string> => {
      const { data, error } = await supabase
        .from("system_config")
        .select("value")
        .eq("key", key)
        .maybeSingle();
      if (error) throw error;
      if (!data?.value) return "";
      // jsonb-strings can come back as either '"value"' wrapped or raw — håndter begge
      const v = data.value;
      if (typeof v === "string") return v;
      if (typeof v === "number" || typeof v === "boolean") return String(v);
      return "";
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Upserts a string value into system_config. Lagres som jsonb-string.
 * Tom verdi lagres som null (sletter effektivt verdien).
 */
export function useUpdateSystemConfigString() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();

  return useMutation({
    mutationFn: async (params: { key: SystemConfigKey; value: string }) => {
      const cleaned = params.value.trim();
      const row: Partial<SystemConfigRow> = {
        key: params.key,
        value: cleaned || null,
        updated_by: activeUser?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from("system_config")
        .upsert(row, { onConflict: "key" });
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateFor(queryClient, "system_config", { key: variables.key });
      logEvent({
        event_type: "system_config.updated",
        entity_type: "system_config",
        entity_id: variables.key,
        user_id: activeUser?.id,
        payload: { key: variables.key, value: variables.value },
      });
    },
  });
}
